/**
 * Topic Moderation Tools Component
 * 
 * Shows moderation actions (lock, pin, delete, move) for staff with proper permissions
 */

import React from 'react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Shield,
  Lock,
  LockOpen,
  Pin,
  PinOff,
  Trash2,
  MoveRight,
  AlertTriangle,
} from 'lucide-react'
import { usePermissions } from '@/hooks/usePermissions'
import {
  FORUM_VIEW_STAFF_TOOLS,
  FORUM_LOCK_TOPICS,
  FORUM_UNLOCK_TOPICS,
  FORUM_PIN_TOPICS,
  FORUM_UNPIN_TOPICS,
  FORUM_DELETE_TOPICS,
  FORUM_MOVE_TOPICS,
} from '@/lib/constants/staff-permissions'

interface TopicModToolsProps {
  topicId: string
  isLocked: boolean
  isPinned: boolean
  boardId: string
  onLock: () => Promise<void>
  onUnlock: () => Promise<void>
  onPin: () => Promise<void>
  onUnpin: () => Promise<void>
  onDelete: () => void
  onMove: () => Promise<void>
}

export function TopicModTools({
  topicId,
  isLocked,
  isPinned,
  boardId,
  onLock,
  onUnlock,
  onPin,
  onUnpin,
  onDelete,
  onMove,
}: TopicModToolsProps) {
  const { hasPermission } = usePermissions()

  // Check individual permissions
  const canViewStaffTools = hasPermission(FORUM_VIEW_STAFF_TOOLS)
  const canLock = hasPermission(FORUM_LOCK_TOPICS)
  const canUnlock = hasPermission(FORUM_UNLOCK_TOPICS)
  const canPin = hasPermission(FORUM_PIN_TOPICS)
  const canUnpin = hasPermission(FORUM_UNPIN_TOPICS)
  const canDelete = hasPermission(FORUM_DELETE_TOPICS)
  const canMove = hasPermission(FORUM_MOVE_TOPICS)

  // Only show if has ANY moderation permission
  const hasAnyPermission = canViewStaffTools || canLock || canUnlock || canPin || canUnpin || canDelete || canMove
  
  if (!hasAnyPermission) {
    return null
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="flex items-center gap-2 bg-warning/10 border-warning/30 text-warning hover:bg-warning/20 hover:border-warning/50 transition-all"
        >
          <Shield className="w-4 h-4" />
          <span className="font-semibold">Moderação</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {/* Lock/Unlock */}
        {isLocked ? (
          canUnlock && (
            <DropdownMenuItem onClick={onUnlock} className="cursor-pointer">
              <LockOpen className="w-4 h-4 mr-2 text-success" />
              <span>Destrancar Tópico</span>
            </DropdownMenuItem>
          )
        ) : (
          canLock && (
            <DropdownMenuItem onClick={onLock} className="cursor-pointer">
              <Lock className="w-4 h-4 mr-2 text-warning" />
              <span>Trancar Tópico</span>
            </DropdownMenuItem>
          )
        )}

        {/* Pin/Unpin */}
        {isPinned ? (
          canUnpin && (
            <DropdownMenuItem onClick={onUnpin} className="cursor-pointer">
              <PinOff className="w-4 h-4 mr-2 text-muted-foreground" />
              <span>Desafixar Tópico</span>
            </DropdownMenuItem>
          )
        ) : (
          canPin && (
            <DropdownMenuItem onClick={onPin} className="cursor-pointer">
              <Pin className="w-4 h-4 mr-2 text-primary" />
              <span>Fixar Tópico</span>
            </DropdownMenuItem>
          )
        )}

        {/* Move */}
        {canMove && (
          <DropdownMenuItem onClick={onMove} className="cursor-pointer">
            <MoveRight className="w-4 h-4 mr-2 text-accent" />
            <span>Mover Tópico</span>
          </DropdownMenuItem>
        )}

        {/* Separator before destructive actions */}
        {canDelete && <DropdownMenuSeparator />}

        {/* Delete */}
        {canDelete && (
          <DropdownMenuItem
            onClick={onDelete}
            className="cursor-pointer text-destructive focus:text-destructive focus:bg-destructive/10"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            <span>Deletar Tópico</span>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
